import React, { Dispatch, SetStateAction } from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import {
  Avatar,
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  Button,
  partitionAvatarGroupItems,
  Label,
  makeStyles,
} from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import {
  iconButtonStyles,
  labelStyles,
  AvatarGroupStyles,
} from '../../../../app/common/styles/CommonStyleObjects';
import StatDateFormats from '../../../../utils/types/DateFormats';
import { IMilestone, MilestoneStatus } from '../Interfaces';
import MilestoneRuleChecklist from '../MilestoneRuleChecklist';
import SpanWithLabel from '../../../common/SpanWithLabel';
import { CanEditDueDate } from '../panels/milestoneDueDateUtils';
import CanAccess from '../../../common/canAccess/CanAccess';
import momentUtc from '../../../../utils/DateFormatter';

interface IMilestoneContentProps {
  milestone: IMilestone;
  openDueDateEditPanel: (focusedMilestoneId: number) => void;
  toggleDividendsPanel: () => void;
  setViewComments: Dispatch<SetStateAction<boolean>>;
  viewComments: boolean;
}

const useContentStyles = makeStyles({
  userAssignment: { margin: '0 5px', width: '180px', overflow: 'hidden' },
  dueDate: { margin: '0 5px', width: '120px' },
  category: { margin: '0 5px', width: '180px' },
  rules: { margin: '0 0 0 5px' },
});

const MilestoneContent = ({
  milestone,
  openDueDateEditPanel,
  toggleDividendsPanel,
  setViewComments,
  viewComments,
}: IMilestoneContentProps): JSX.Element => {
  const labelClasses = labelStyles();
  const styles = AvatarGroupStyles();
  const contentClasses = useContentStyles();

  const personas =
    milestone.assignees?.map((signoffRole, index) => ({
      name: signoffRole.name,
      id: index.toString(),
    })) ?? [];
  // Fluent UI v9 best practice: use partitionAvatarGroupItems to split the
  // assignees into inline avatars and overflow items rendered in a popover.
  const { inlineItems, overflowItems } = partitionAvatarGroupItems({
    items: personas,
    maxInlineItems: 3,
  });

  const renderUserAssignment = (): JSX.Element => {
    return (
      <div className={styles.assigneeContainer}>
        <Label className={labelClasses.root}>Assignee(s)</Label>
        {personas.length > 0 ? (
          <AvatarGroup className={styles.avatarGroup}>
            {inlineItems.map((item) => (
              <AvatarGroupItem name={item.name} key={item.id} />
            ))}
            {(overflowItems?.length ?? 0) > 0 && (
              <AvatarGroupPopover>
                {overflowItems.map((item) => (
                  <AvatarGroupItem name={item.name} key={item.id} />
                ))}
              </AvatarGroupPopover>
            )}
          </AvatarGroup>
        ) : (
          <Avatar className={styles.avatarGroup} initials="NA" color="anchor" name="Not Assigned" />
        )}
      </div>
    );
  };

  const renderDueDatePicker = (dueDate: Date, milestoneId: number): JSX.Element => {
    const editDueDatesLabel = `Edit Milestone Due Dates — ${milestone.title}`;
    return (
      <Stack horizontal verticalAlign="baseline">
        <SpanWithLabel
          spanOverrideStyles={{ paddingTop: 6 }}
          labelText="Due Date"
          value={momentUtc(dueDate, StatDateFormats.DaySimple)}
        />
        <CanAccess requestedAction="MilestoneDueDates:Update">
          {CanEditDueDate(milestone) && (
            <Button
              appearance="subtle"
              title={editDueDatesLabel}
              aria-label={editDueDatesLabel}
              icon={<EditRegular color={iconButtonStyles.color} />}
              onClick={(): void => {
                openDueDateEditPanel(milestoneId);
              }}
            />
          )}
        </CanAccess>
      </Stack>
    );
  };

  const userAssignmentClassName = contentClasses.userAssignment;
  const dueDateClassName = contentClasses.dueDate;
  const categoryClassName = contentClasses.category;
  const rulesClassName = contentClasses.rules;
  return (
    <Stack wrap>
      <Stack horizontal wrap>
        <StackItem className={userAssignmentClassName}>{renderUserAssignment()}</StackItem>
        <StackItem className={dueDateClassName}>
          {renderDueDatePicker(milestone.dueDate, milestone.id)}
        </StackItem>
        {milestone.category && (
          <StackItem className={categoryClassName}>
            <SpanWithLabel
              spanOverrideStyles={{ paddingTop: 6 }}
              labelText="Category"
              value={milestone.category}
            />
          </StackItem>
        )}
        <Stack className={rulesClassName} grow={1}>
          <Stack verticalAlign="center" horizontalAlign="end" verticalFill grow={1}>
            <MilestoneRuleChecklist
              isMilestoneComplete={milestone.status === MilestoneStatus.complete}
              milestoneRules={milestone.milestoneRules}
              disabled={milestone.status !== MilestoneStatus.complete}
              milestoneId={milestone.id}
              milestoneTitle={milestone.title}
              toggleDividendsPanel={toggleDividendsPanel}
              commentsVisible={viewComments}
              setCommentsVisible={setViewComments}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MilestoneContent;
